import { type ReactNode, type RefObject, useEffect, useRef } from 'react';
import clsx from 'clsx';
import styles from './NativeDialogBase.module.css';

// This component expects the caller to show/hide modal via dialog.showModal and dialog.close methods rather than isOpen and onDismiss properties. isOpena and onDismiss can be used with <NativeDialogWrapper />. See storybook for examples.

export type NativeDialogBaseProps = {
  children: ReactNode;
  ariaLabel: string;
  testid?: string;
  size: 'medium' | 'large' | 'full-screen';
  containerClassName?: string;
  bodyClassName?: string;
};

type NativeDialogComponentProps = NativeDialogBaseProps & {
  dialogRef: RefObject<HTMLDialogElement>;
};

export function NativeDialogBase(props: NativeDialogComponentProps) {
  const {
    dialogRef,
    children,
    size,
    containerClassName,
    bodyClassName,
    testid,
  } = props;

  const innerRef = useRef<HTMLDivElement>(null);

  // Close on click outside dialog element
  useEffect(() => {
    if (!dialogRef.current) {
      return;
    }

    const dialog = dialogRef.current;

    const onDialogClick = (e: MouseEvent) => {
      const rect = dialog.getBoundingClientRect();

      const isClickInsideRect =
        rect.top <= e.clientY &&
        e.clientY <= rect.top + rect.height &&
        rect.left <= e.clientX &&
        e.clientX <= rect.left + rect.width;

      if (!isClickInsideRect) {
        dialog.close();
      }
    };
    dialog.addEventListener('click', onDialogClick);

    return () => dialog.removeEventListener('click', onDialogClick);
  }, [dialogRef]);

  return (
    <dialog
      ref={dialogRef}
      data-testid={testid}
      className={clsx(styles.dialog, styles[size], containerClassName)}
    >
      <div ref={innerRef} className={bodyClassName}>
        {children}
      </div>
    </dialog>
  );
}
